// C:\Users\aleja\Code\inventory_analytics\src\front\src\pages\OrmaCatalogAdmin.jsx
import React, { useContext, useEffect, useRef, useState } from "react";
import { Context } from "../store/appContext";
import "../styles/layout.css";
import "../styles/utilities.css";
import "../styles/forms.css";
import "../styles/catalog.css";

const numberFormatter = new Intl.NumberFormat("es-ES", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const integerFormatter = new Intl.NumberFormat("es-ES", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const formatNumber = (value) => numberFormatter.format(Number(value || 0));
const formatInteger = (value) => integerFormatter.format(Number(value || 0));

const formatDateTime = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getErrorMessage = (result, fallback) =>
  result?.error || result?.msg || fallback;

const hasXlsxExtension = (file) =>
  Boolean(file?.name && file.name.toLowerCase().endsWith(".xlsx"));

const FieldValue = ({ label, value }) => (
  <div className="orma-detail-field">
    <span>{label}</span>
    <strong title={value || ""}>{value || "-"}</strong>
  </div>
);

const getLoadState = (meta, error) => {
  if (error) return "missing";
  if (!meta) return "loading";
  if (meta.status === "failed") return "failed";
  if (Number(meta.coverage_percent || 0) < 100) return "partial";
  return "ok";
};

const LOAD_STATE_COPY = {
  loading: {
    label: "Consultando carga",
    text: "Recuperando el estado administrativo del catálogo.",
  },
  ok: {
    label: "Carga correcta",
    text: "La última carga ORMA está disponible para consulta.",
  },
  partial: {
    label: "Cobertura parcial",
    text: "La carga está disponible aunque no todos los materiales VISOR tienen coincidencia MAESTRO.",
  },
  missing: {
    label: "Sin carga disponible",
    text: "No hay una carga ORMA usable para administrar.",
  },
  failed: {
    label: "Carga fallida",
    text: "La última carga registrada contiene errores.",
  },
};

const OrmaCatalogAdmin = () => {
  const { store, actions } = useContext(Context);
  const [meta, setMeta] = useState(null);
  const [metaLoading, setMetaLoading] = useState(false);
  const [metaError, setMetaError] = useState("");
  const [visorFile, setVisorFile] = useState(null);
  const [maestroFile, setMaestroFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState("");
  const [importResult, setImportResult] = useState(null);
  const importFormRef = useRef(null);

  const isAdmin = store.role === "admin";

  const loadMeta = async (signal) => {
    setMetaLoading(true);
    setMetaError("");

    const result = await actions.getOrmaCatalogMeta({ signal });

    if (result?.aborted) return;

    if (!result.success) {
      setMeta(null);
      setMetaError(
        result.status === 404
          ? "No existe todavía una carga ORMA completada."
          : getErrorMessage(result, "No se pudo cargar el estado del catálogo ORMA"),
      );
      setMetaLoading(false);
      return;
    }

    setMeta(result.data || null);
    setMetaLoading(false);
  };

  useEffect(() => {
    const controller = new AbortController();
    loadMeta(controller.signal);

    return () => controller.abort();
  }, []);

  const handleImport = async (event) => {
    event.preventDefault();
    if (importLoading || !isAdmin) return;

    setImportError("");
    setImportResult(null);

    if (!visorFile || !maestroFile) {
      setImportError("Selecciona los archivos VISOR y MAESTRO.");
      return;
    }

    if (!hasXlsxExtension(visorFile) || !hasXlsxExtension(maestroFile)) {
      setImportError("Los archivos VISOR y MAESTRO deben tener extensión .xlsx.");
      return;
    }

    const formData = new FormData();
    formData.append("visor_file", visorFile);
    formData.append("maestro_file", maestroFile);

    setImportLoading(true);
    const result = await actions.importOrmaCatalog(formData);
    setImportLoading(false);

    if (!result.success) {
      setImportError(getErrorMessage(result, "No se pudo importar el catálogo ORMA"));
      return;
    }

    setImportResult(result.data || {});
    setVisorFile(null);
    setMaestroFile(null);
    importFormRef.current?.reset();

    const controller = new AbortController();
    await loadMeta(controller.signal);
  };

  const stateKey = getLoadState(meta, metaError);
  const stateCopy = LOAD_STATE_COPY[stateKey];

  return (
    <div className="container mt-5 catalog-page orma-catalog-page orma-admin-page">
      <div className="page-header catalog-header">
        <div className="catalog-title-block">
          <span className="catalog-title-icon" aria-hidden="true">
            <i className="bi bi-database-gear" />
          </span>
          <div>
            <h1 className="page-title">Administración ORMA</h1>
            <p className="page-subtitle">
              Estado de carga, cobertura VISOR/MAESTRO e importación administrativa.
            </p>
          </div>
        </div>
      </div>

      <section className={`catalog-card orma-admin-status orma-admin-status-${stateKey}`}>
        <div className="catalog-section-head">
          <div>
            <h2>{stateCopy.label}</h2>
            <p>{stateCopy.text}</p>
          </div>
          {metaLoading && <span className="catalog-table-count">Cargando...</span>}
        </div>

        {metaError ? (
          <div className="catalog-state catalog-state-empty catalog-state-inline" aria-live="polite">
            {metaError}
          </div>
        ) : (
          <div className="orma-freshness-summary">
            <FieldValue label="Estado de carga" value={meta?.status} />
            <FieldValue label="Fecha de carga" value={formatDateTime(meta?.loaded_at)} />
            <FieldValue label="Usuario" value={meta?.loader?.username || meta?.loaded_by} />
            <FieldValue label="Cobertura" value={`${formatNumber(meta?.coverage_percent)}%`} />
          </div>
        )}
      </section>

      <section className="catalog-card orma-admin-metrics">
        <div className="catalog-section-head">
          <div>
            <h2>Detalle de carga</h2>
            <p>Resumen técnico de la última carga ORMA usable.</p>
          </div>
        </div>
        <div className="orma-freshness-grid">
          <FieldValue label="Archivo VISOR" value={meta?.visor_filename} />
          <FieldValue label="Archivo MAESTRO" value={meta?.maestro_filename} />
          <FieldValue label="Filas VISOR" value={formatInteger(meta?.visor_rows)} />
          <FieldValue label="Filas MAESTRO" value={formatInteger(meta?.maestro_rows)} />
          <FieldValue label="Materiales catálogo" value={formatInteger(meta?.catalog_rows)} />
          <FieldValue label="Coincidencias" value={formatInteger(meta?.matched_rows)} />
          <FieldValue label="VISOR sin MAESTRO" value={formatInteger(meta?.visor_without_master)} />
          <FieldValue label="MAESTRO sin VISOR" value={formatInteger(meta?.master_without_visor)} />
          <FieldValue label="Errores de carga" value={meta?.error_message || "Sin errores registrados"} />
        </div>
      </section>

      {isAdmin && (
        <section className="catalog-card orma-import-card" aria-label="Importación ORMA admin">
          <div className="catalog-section-head">
            <div>
              <h2>Importación ORMA</h2>
              <p>Selecciona un archivo VISOR y un archivo MAESTRO en formato .xlsx.</p>
            </div>
          </div>
          <form ref={importFormRef} className="orma-import-form" onSubmit={handleImport}>
            <div className="catalog-filter-group">
              <label htmlFor="orma_admin_visor_file">Archivo VISOR</label>
              <input
                id="orma_admin_visor_file"
                className="form-control"
                type="file"
                accept=".xlsx"
                onChange={(event) => setVisorFile(event.target.files?.[0] || null)}
                disabled={importLoading}
              />
            </div>
            <div className="catalog-filter-group">
              <label htmlFor="orma_admin_maestro_file">Archivo MAESTRO</label>
              <input
                id="orma_admin_maestro_file"
                className="form-control"
                type="file"
                accept=".xlsx"
                onChange={(event) => setMaestroFile(event.target.files?.[0] || null)}
                disabled={importLoading}
              />
            </div>
            <button className="btn btn-primary" type="submit" disabled={importLoading}>
              {importLoading ? "Importando" : "Importar catálogo"}
            </button>
          </form>

          <div aria-live="polite">
            {importError && (
              <div className="catalog-state catalog-state-error catalog-state-inline">
                {importError}
              </div>
            )}
            {importResult && (
              <div className="orma-import-result">
                <FieldValue label="Carga" value={importResult.load_id} />
                <FieldValue label="Filas VISOR" value={formatInteger(importResult.visor_rows)} />
                <FieldValue label="Filas MAESTRO" value={formatInteger(importResult.maestro_rows)} />
                <FieldValue label="Materiales catálogo" value={formatInteger(importResult.catalog_rows)} />
                <FieldValue label="Coincidencias" value={formatInteger(importResult.matched_rows)} />
                <FieldValue label="Cobertura" value={`${formatNumber(importResult.coverage_percent)}%`} />
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
};

export default OrmaCatalogAdmin;
